import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { BadgePlus, SquarePen } from 'lucide-react';
import { GiftRequestDTO } from '@/hooks/useGiftsRequests';
import {
  useCreateGiftRequest,
  useUpdateGiftRequest,
} from '@/hooks/useGiftsRequests';

import {
  giftRequestFormSchema,
  GiftRequestFormValues,
} from '@/schemas/gift-request';
import LoadingCircle from '@/components/LoadingCircle';
import { Textarea } from '@/components/ui/textarea';
import { supplierStatus } from '@/api/suppliers';
import { GiftDTO } from '@/hooks/useGifts';
import TextInActive from '@/components/TextInActive';

import TextUserInactive from '@/components/TextUserInactive';
import { GiftStatus } from '@/api/gifts';
import { IEmployee, userStatus } from '@/api/employee';
import { ICustomer } from '@/api/customers';
import { useProfileContext } from '@/hooks/useProfileContext';
import { Input } from '@/components/ui/input';
import { GiftRequestStatus } from '@/api/gifts-requests';
import {
  formatCurrency,
  formatCurrencyWithoutSymbol,
  formatDateWithTime,
} from '@/lib/utils';

interface CreateUpdateProps {
  giftRequest?: GiftRequestDTO;
  giftsData: GiftDTO[];
  customersData: ICustomer[];
  employeesData: IEmployee[];
  refetchGifts: () => void;
}

const CreateUpdate = ({
  giftRequest,
  giftsData,
  customersData,
  employeesData,
  refetchGifts,
}: CreateUpdateProps) => {
  const { profile } = useProfileContext();
  const isSeller = profile?.role?.key === 'marketing';
  const isManager = profile?.role?.key === 'manager-marketing';

  const [open, setOpen] = useState(false);
  const isEdit = !!giftRequest;

  const { mutateAsync: createGiftRequest, isPending } = useCreateGiftRequest();
  const { mutateAsync: updateGiftRequest, isPending: isUpdating } =
    useUpdateGiftRequest();

  const form = useForm<GiftRequestFormValues>({
    resolver: zodResolver(
      giftRequestFormSchema(isEdit, giftRequest?.reasonReject !== null)
    ),
    defaultValues: {
      giftId: giftRequest?.gift.id.toString() || '',
      customerIds:
        giftRequest?.customers.map((customer) => customer.id.toString()) || [],
      reason: giftRequest?.reason || '',
      numberOfGiftsPerCustomer:
        giftRequest?.numberOfGiftsPerCustomer || undefined,
      assignedToId: isSeller
        ? profile?.id.toString()
        : isEdit
        ? giftRequest?.assignedTo.id.toString() || ''
        : isManager
        ? profile?.id.toString()
        : '',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (giftRequest) {
      form.reset({
        giftId: giftRequest.gift.id.toString(),
        customerIds: giftRequest.customers.map((customer) =>
          customer.id.toString()
        ),
        reason: giftRequest.reason,
        assignedToId: giftRequest.assignedTo.id.toString(),
        numberOfGiftsPerCustomer:
          giftRequest.numberOfGiftsPerCustomer || undefined,
      });
    }
  }, [giftRequest, form]);

  async function onSubmit(values: GiftRequestFormValues) {
    console.log(values);
    const payload = {
      giftId: parseInt(values.giftId),
      customerIds: values.customerIds.map((id) => parseInt(id)),
      reason: values.reason,
      assignedToId: parseInt(values.assignedToId),
      numberOfGiftsPerCustomer: values.numberOfGiftsPerCustomer,
      explanation: values.explanation,
    };

    if (isEdit) {
      await updateGiftRequest({
        id: giftRequest.id,
        data: payload,
      });
    } else {
      await createGiftRequest(payload);
    }
    setOpen(false);
    form.reset();
  }

  const [selectedAssignedTo, setSelectedAssignedTo] = useState<string>(
    form.getValues('assignedToId') || ''
  );

  // Watch form values for real-time calculations
  const watchedValues = form.watch([
    'numberOfGiftsPerCustomer',
    'customerIds',
    'giftId',
  ]);
  const [numberOfGiftsPerCustomer, customerIds, giftId] = watchedValues;

  const checkUserReject = (giftRequest: GiftRequestDTO) => {
    switch (giftRequest.status) {
      case GiftRequestStatus.BUSINESS_REJECTED:
        return 'Quản lý';
      case GiftRequestStatus.ADMIN_REJECTED:
        return 'Admin';
      case GiftRequestStatus.GENERAL_DIRECTOR_REJECTED:
        return 'Giám đốc';
      default:
        return '';
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        form.reset();
        setOpen(value);
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant={isEdit ? 'outline' : 'default'}
          className={isEdit ? 'luxury-button-outline' : 'luxury-button-primary'}
          onClick={() => {
            refetchGifts();
          }}
        >
          {isEdit ? (
            <>
              <SquarePen className="w-4 h-4" />
              Sửa
            </>
          ) : (
            <>
              <BadgePlus className="w-4 h-4" />
              Thêm mới
            </>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="min-w-[800px] max-h-[95vh] overflow-y-auto luxury-theme">
        <DialogDescription className="sr-only">
          {isEdit ? 'Sửa yêu cầu quà' : 'Thêm yêu cầu quà'}
        </DialogDescription>
        <DialogHeader className="luxury-header">
          <DialogTitle className="luxury-title">
            {isEdit ? 'Cập nhật yêu cầu quà' : 'Tạo yêu cầu quà mới'}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-8"
            id="create-update-form"
          >
            <div className="luxury-card">
              <h3 className="luxury-subtitle mb-4">Thông tin yêu cầu quà</h3>
              <div className="flex flex-col gap-6 items-start">
                <div className="flex gap-6 w-full items-start">
                  <FormField
                    control={form.control}
                    name="giftId"
                    render={({ field }) => (
                      <FormItem className="w-1/2">
                        <Label required className="luxury-label">
                          Quà
                        </Label>
                        <FormControl>
                          <Select
                            value={field.value.toString()}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger className="w-full luxury-input">
                              <SelectValue placeholder="Chọn quà" />
                            </SelectTrigger>
                            <SelectContent className="border-luxury-200">
                              {giftsData
                                ?.filter(
                                  (gift) =>
                                    (gift.supplier.status ===
                                      supplierStatus.ACTIVE &&
                                      gift.status === GiftStatus.ACTIVE) ||
                                    (isEdit &&
                                      gift.id.toString() ===
                                        giftRequest?.gift.id.toString())
                                )
                                .map((gift) => (
                                  <SelectItem
                                    key={gift.id}
                                    value={gift.id.toString()}
                                    disabled={
                                      gift.supplier.status ===
                                        supplierStatus.INACTIVE ||
                                      gift.status === GiftStatus.INACTIVE
                                    }
                                  >
                                    {gift.name}
                                    <TextInActive
                                      status={
                                        gift.supplier.status ===
                                        supplierStatus.INACTIVE
                                          ? supplierStatus.INACTIVE
                                          : gift.status
                                      }
                                    />
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                        <div className="text-sm text-luxury-500">
                          <span className="text-black">
                            Số lượng quà còn lại:
                          </span>{' '}
                          <span className="font-bold">
                            {formatCurrencyWithoutSymbol(
                              giftsData.find(
                                (gift) => gift.id.toString() === field.value
                              )?.remainingQuantity || 0
                            )}
                          </span>
                        </div>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="assignedToId"
                    render={({ field }) => (
                      <FormItem className="w-1/2">
                        <Label required className="luxury-label">
                          Nhân viên phụ trách
                        </Label>
                        <FormControl>
                          <Select
                            value={field.value.toString()}
                            onValueChange={(value) => {
                              field.onChange(value);
                              setSelectedAssignedTo(value);
                              form.setValue('customerIds', []);
                            }}
                            disabled={isSeller}
                          >
                            <SelectTrigger className="w-full luxury-input">
                              <SelectValue placeholder="Chọn nhân viên" />
                            </SelectTrigger>
                            <SelectContent className="border-luxury-200">
                              {isSeller ? (
                                <SelectItem
                                  value={profile?.id?.toString() || ''}
                                >
                                  {profile?.name}
                                </SelectItem>
                              ) : (
                                employeesData
                                  .filter(
                                    (employee) =>
                                      employee.status === userStatus.ACTIVE
                                  )
                                  .map((employee) => (
                                    <SelectItem
                                      key={employee.id}
                                      value={employee.id.toString()}
                                    >
                                      {employee.name}
                                      <TextUserInactive
                                        status={employee.status}
                                      />
                                    </SelectItem>
                                  ))
                              )}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="flex gap-6 w-full items-start">
                  <FormField
                    control={form.control}
                    name="customerIds"
                    render={({ field }) => (
                      <FormItem className="w-1/2">
                        <Label required className="luxury-label">
                          Khách hàng được tặng
                        </Label>
                        <FormControl>
                          <Select value="" onValueChange={() => {}}>
                            <SelectTrigger className="w-full luxury-input">
                              <div className="flex flex-wrap gap-1 flex-1 justify-start">
                                {field.value.length > 0 ? (
                                  field.value.length > 2 ? (
                                    <span className="text-sm">
                                      Đã chọn {field.value.length} khách hàng
                                    </span>
                                  ) : (
                                    field.value.map((customerId) => {
                                      const customer = customersData?.find(
                                        (c) => c.id.toString() === customerId
                                      );
                                      return customer ? (
                                        <span
                                          key={customerId}
                                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-luxury-100 text-luxury-800 rounded-md"
                                        >
                                          KH{customer.id} - {customer.fullName}
                                        </span>
                                      ) : null;
                                    })
                                  )
                                ) : (
                                  <span className="text-muted-foreground">
                                    Chọn khách hàng
                                  </span>
                                )}
                              </div>
                            </SelectTrigger>
                            <SelectContent className="border-luxury-200">
                              {/* Select All / Deselect All buttons */}
                              <div className="flex gap-2 p-2 border-b border-luxury-200">
                                <button
                                  type="button"
                                  className="flex-1 px-3 py-1 text-xs bg-luxury-600 text-white rounded hover:bg-luxury-700 transition-colors"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    const allCustomerIds =
                                      customersData
                                        ?.filter(
                                          (customer) =>
                                            (customer.seller.status ===
                                              userStatus.ACTIVE ||
                                              (isEdit &&
                                                giftRequest?.customers.some(
                                                  (c) => c.id === customer.id
                                                ))) &&
                                            customer.seller.id.toString() ===
                                              selectedAssignedTo
                                        )
                                        .map((customer) =>
                                          customer.id.toString()
                                        ) || [];
                                    field.onChange(allCustomerIds);
                                  }}
                                >
                                  Chọn tất cả
                                </button>
                                <button
                                  type="button"
                                  className="flex-1 px-3 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    field.onChange([]);
                                  }}
                                >
                                  Bỏ tất cả
                                </button>
                              </div>

                              {/* Counter showing selected/total */}
                              <div className="px-2 py-1 text-xs text-gray-600 bg-gray-50 border-b border-luxury-200">
                                Đã chọn: {field.value.length} /{' '}
                                {customersData?.filter(
                                  (customer) =>
                                    (customer.seller.status ===
                                      userStatus.ACTIVE ||
                                      (isEdit &&
                                        giftRequest?.customers.some(
                                          (c) => c.id === customer.id
                                        ))) &&
                                    customer.seller.id.toString() ===
                                      selectedAssignedTo
                                ).length || 0}{' '}
                                khách hàng
                              </div>

                              {customersData
                                ?.filter((customer) => {
                                  return (
                                    (customer.seller.status ===
                                      userStatus.ACTIVE ||
                                      (isEdit &&
                                        giftRequest?.customers.some(
                                          (c) => c.id === customer.id
                                        ))) &&
                                    customer.seller.id.toString() ===
                                      selectedAssignedTo
                                  );
                                })
                                .map((customer) => (
                                  <div
                                    key={customer.id}
                                    className="flex items-center space-x-2 p-2 hover:bg-luxury-50 cursor-pointer"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      const currentValue = field.value || [];
                                      const customerIdStr =
                                        customer.id.toString();
                                      const isSelected =
                                        currentValue.includes(customerIdStr);

                                      let newValue;
                                      if (isSelected) {
                                        newValue = currentValue.filter(
                                          (id) => id !== customerIdStr
                                        );
                                      } else {
                                        newValue = [
                                          ...currentValue,
                                          customerIdStr,
                                        ];
                                      }
                                      field.onChange(newValue);
                                    }}
                                  >
                                    <Checkbox
                                      checked={field.value.includes(
                                        customer.id.toString()
                                      )}
                                      className="luxury-checkbox"
                                    />
                                    <span className="text-sm">
                                      KH{customer.id} - {customer.fullName}
                                    </span>
                                  </div>
                                ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="numberOfGiftsPerCustomer"
                    render={({ field }) => (
                      <FormItem className="w-1/2">
                        <Label required className="luxury-label">
                          Số lượng quà trên mỗi khách hàng
                        </Label>
                        <FormControl>
                          <Input
                            value={field.value || ''}
                            type="number"
                            className="luxury-input"
                            min="1"
                            onChange={(e) =>
                              field.onChange(parseInt(e.target.value))
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="reason"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <Label required className="luxury-label">
                        Lý do
                      </Label>
                      <FormControl>
                        <Textarea
                          {...field}
                          className="luxury-input"
                          rows={4}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="luxury-card my-4">
                <h3 className="luxury-subtitle">Thông tin tổng giá trị</h3>
                <div className="flex flex-col gap-6 items-start">
                  <div className="flex gap-6 w-full items-start">
                    <div className="flex flex-col gap-2">
                      <span className="luxury-label">Tổng giá trị (VNĐ)</span>
                      <span className="font-bold text-luxury-900">
                        {formatCurrency(
                          (numberOfGiftsPerCustomer || 0) *
                            (customerIds?.length || 0) *
                            (giftsData?.find(
                              (gift) => gift.id.toString() === giftId
                            )?.unitPrice || 0)
                        )}
                      </span>
                    </div>
                    <div className="flex flex-col gap-2">
                      <span className="luxury-label">Số lượng quà yêu cầu</span>
                      <span className="font-bold text-luxury-900">
                        {formatCurrencyWithoutSymbol(
                          (numberOfGiftsPerCustomer || 0) *
                            (customerIds?.length || 0)
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {isEdit && giftRequest?.reasonReject !== null && (
                <div className="flex w-full items-start flex-col mt-4">
                  <div className="p-4 rounded-md w-full mb-4 luxury-card !shadow-none">
                    <Label className="luxury-label">
                      Yêu cầu tặng quà của bạn bị từ chối bởi{' '}
                      <span className="font-bold">
                        {giftRequest?.usedRejectBy?.username}
                        {' - '}
                        {checkUserReject(giftRequest)}
                      </span>{' '}
                      với lý do:
                    </Label>
                    <div className="text-base font-medium  whitespace-pre-line break-words w-full mt-1">
                      {giftRequest?.reasonReject} vào lúc{' '}
                      {formatDateWithTime(giftRequest?.timeReject || '')}
                    </div>
                  </div>
                  <FormField
                    control={form.control}
                    name="explanation"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <Label required className="luxury-label">
                          Lý do muốn được phê duyệt lại
                        </Label>
                        <FormControl>
                          <Textarea
                            {...field}
                            className="min-h-[100px] luxury-input bg-white"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </div>

            <Button
              type="submit"
              className="w-full mt-6 luxury-button-primary"
              disabled={isPending || isUpdating}
            >
              {isPending || isUpdating ? (
                <LoadingCircle />
              ) : (
                <>{isEdit ? 'Cập nhật yêu cầu quà' : 'Tạo yêu cầu quà mới'}</>
              )}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateUpdate;
